import React, { FC } from 'react';
import { FluidObject } from 'gatsby-image';
import styled from 'styled-components';

import { AuthorByIdQuery } from '../../../graphql-types';
import { floralwhite, primaryColor, secondaryColor, media, primaryColorLight } from '../../utils/styles';
import { rhythm } from '../../utils/typography';

import { Section, SectionBody } from '../Layout';
import { Image } from '../Image';
import { StyledButton } from '../Form';
import { makeTabbable } from '../../utils/a11y';
import { focusOn } from '../Form/helpers';
import { useEventTracker } from '../../utils/analytics/useEventTracker';

const PICTURE_HEIGHT = '160px';
const HERO_HEIGHT = '120px';

const AuthorHeroWrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const AuthorHeroSection = styled(Section)`
  height: ${HERO_HEIGHT};
  margin-bottom: calc(${PICTURE_HEIGHT} / 2 + ${rhythm(1)});
  ${SectionBody} {
    overflow: visible;
  }
`;

const AuthorPicture = styled(Image)`
  height: ${PICTURE_HEIGHT};
  width: ${PICTURE_HEIGHT};
  border-radius: 100px;
  border: solid 3px ${primaryColor};
`;

const AuthorHeroContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(60px);
`;

const AuthorDetails = styled.div`
  flex: 1;
  display: block;
  text-align: center;
  margin-bottom: ${rhythm(1 / 2)};
`;

const AuthorHr = styled.hr`
  display: inline-block;
  background-color: ${secondaryColor};
  height: 2px;
  width: 120px;
`;
const AuthorName = styled.h1``;
const JobTitle = styled.h3`
  margin-bottom: ${rhythm(1 / 3)};
`;

const AskButton = styled(StyledButton)`
  display: block;
  margin: ${rhythm(1)} ${rhythm(1)} 0 ${rhythm(1)};
  ${media.mobile`align-self: initial;`}
`;

const WorkButton = styled(AskButton)`
  border: solid ${primaryColorLight} 1px;
  color: ${primaryColor};
  &:focus,
  &:hover {
    border: solid ${primaryColor} 1px;
  }
`;

const AuthorActions = styled.div`
  display: flex;
  justify-content: center;
  ${media.mobile`flex-direction: column;`}
`;

export type AuthorHeroProps = {
  author: AuthorByIdQuery['author'];
};

export const AuthorHero: FC<AuthorHeroProps> = ({ author }) => {
  const { name, jobTitle, picture, slug, jobOpenings } = author?.data || {};
  const [localFile] = picture?.localFiles || [];
  const pictureFluid = localFile?.childImageSharp?.fluid;
  const trackClick = useEventTracker('click');

  const handleQuestion = () => {
    trackClick({
      category: 'engagement',
      label: `ask-${slug}`,
    });
    focusOn('email');
  };

  const handleWorkWithMe = () => {
    trackClick({
      category: 'engagement',
      label: `work-with-${slug}`,
    });

    jobOpenings && window.open(jobOpenings, '_blank');
  };

  return (
    <AuthorHeroWrapper>
      <AuthorHeroSection color={floralwhite}>
        <AuthorHeroContent>{pictureFluid && <AuthorPicture fluid={pictureFluid as FluidObject} />}</AuthorHeroContent>
      </AuthorHeroSection>
      <AuthorDetails>
        <AuthorName>{name}</AuthorName>
        <AuthorHr />
        <JobTitle>{jobTitle}</JobTitle>
        <AuthorActions>
          <AskButton {...makeTabbable(handleQuestion)}>💬 ask me anything</AskButton>
          {jobOpenings && <WorkButton {...makeTabbable(handleWorkWithMe)}>⚡️ work with me</WorkButton>}
        </AuthorActions>
      </AuthorDetails>
    </AuthorHeroWrapper>
  );
};
